import { useState, useEffect } from 'react';
import { useApp } from '../../contexts/AppContext';
import { Button } from '../../components/ui';
import { AnimatedPlaceholder, ExampleChips } from '../../components/express';
import { AuthModal, TrustBadges } from '../../components/shared';
import { useAnimatedPlaceholder, useIntentAnalysis } from '../../hooks';
import { validateTextLength } from '../../utils/validation';
import { generateExpressPrompt } from '../../utils/promptGenerator';
import { track, EVENTS } from '../../utils/analytics';
import type { UserType } from '../../types';

const PLACEHOLDER_EXAMPLES = [
  "I run a landscaping company and want to set aside 30% for taxes before paying expenses...",
  "Help me separate my Shopify revenue into profit, taxes, operating costs, and owner's pay...",
  "I'm a consultant making $15K/month - I need to pay myself a salary and manage business expenses...",
  "I run a small agency and want to automate Profit First across all my business accounts...",
];

// Short labels shown to user, expanded text injected on click
const EXAMPLE_CHIP_MAP: Record<string, string> = {
  "Pay myself first": "I own a small business and all revenue goes into one account. I want to automatically take my owner's pay first - a fixed amount each month - then split what's left between taxes, operating expenses, and a business savings reserve.",
  "Save for taxes": "Every time revenue hits my business account, I need to set aside 25-30% for taxes before I touch anything. After that, distribute the rest to operating expenses, vendor payments, business credit card, and keep a small buffer for slow months.",
  "Separate business & personal": "I need a clear wall between business and personal money. Business revenue should split into tax savings, operating costs, business credit card payments, and owner's pay. My owner's pay then covers rent, utilities, groceries, personal savings, and personal credit card.",
  "Build business reserves": "I want to build a 3-month operating expense cushion for my business. Every time revenue comes in, skim a percentage into a dedicated reserve account until I hit my target. Once the fund is full, redirect that money back to growth and operations.",
  "Prevent expenses overspend": "My business expenses keep getting mixed up with discretionary spending. I need operating costs like rent, software, insurance, and vendor payments locked into their own accounts so I never accidentally dip into money that's already spoken for.",
  "Always be payroll ready": "I have employees and payroll is my top priority. When revenue arrives, payroll gets funded first - every time, no exceptions. After payroll is covered, the rest goes to taxes, operating expenses, and whatever is left goes to owner's pay and business savings.",
};

const EXAMPLE_CHIP_LABELS = Object.keys(EXAMPLE_CHIP_MAP);

export default function ExpressFlow() {
  const { state, setFlow, setExpressText, setExpressUserType, setExpressIncomeRange, setApiRequest } = useApp();
  const { expressInput } = state;
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openFaq, setOpenFaq] = useState<string | null>(null);
  const [inputStarted, setInputStarted] = useState(false);

  useEffect(() => {
    track(EVENTS.EXPRESS_PAGE_VIEWED);
  }, []);

  const { displayText, isTyping } = useAnimatedPlaceholder({
    placeholders: PLACEHOLDER_EXAMPLES,
  });

  const { detectedUserType, detectedIncomeRange } = useIntentAnalysis(expressInput.text);

  // Auto-set detected values
  useEffect(() => {
    if (detectedUserType && !expressInput.userType) {
      setExpressUserType(detectedUserType);
    }
  }, [detectedUserType, expressInput.userType, setExpressUserType]);

  useEffect(() => {
    if (detectedIncomeRange && !expressInput.incomeRange) {
      setExpressIncomeRange(detectedIncomeRange);
    }
  }, [detectedIncomeRange, expressInput.incomeRange, setExpressIncomeRange]);

  function handleTextChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    if (!inputStarted && e.target.value.length > 0) {
      setInputStarted(true);
      track(EVENTS.EXPRESS_INPUT_STARTED, { source: 'typed' });
    }
    setExpressText(e.target.value);
    setError(null);
  }

  function handleExampleClick(label: string) {
    track(EVENTS.EXAMPLE_CHIP_CLICKED, { label });
    if (!inputStarted) {
      setInputStarted(true);
      track(EVENTS.EXPRESS_INPUT_STARTED, { source: 'chip' });
    }
    // Replace input with expanded text (not append)
    const expandedText = EXAMPLE_CHIP_MAP[label] || label;
    setExpressText(expandedText);
  }

  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit() {
    const validationError = validateTextLength(expressInput.text, 20, 1000);
    if (validationError) {
      setError(validationError);
      return;
    }

    setFlow('express');

    const prompt = generateExpressPrompt(expressInput);
    const userType: UserType = expressInput.userType || 'BUSINESS';

    // Check financial relevance using raw user text (not the enriched prompt)
    setSubmitting(true);
    setError(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
      const res = await fetch(`${apiUrl}/api/check-relevance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: expressInput.text }),
      });

      if (res.status === 422) {
        const data = await res.json();
        track(EVENTS.EXPRESS_GUARDRAIL_BLOCKED, { textLength: expressInput.text.length });
        setError(data.detail);
        setSubmitting(false);
        return;
      }

      if (!res.ok) {
        // Non-blocking: if the check itself fails, let the user through
        console.warn('Relevance check failed, proceeding anyway');
      }
    } catch {
      // Network error - don't block the user
      console.warn('Relevance check unreachable, proceeding anyway');
    }
    setSubmitting(false);

    track(EVENTS.EXPRESS_SUBMITTED, {
      textLength: expressInput.text.length,
      userType,
      incomeRange: expressInput.incomeRange,
    });

    setApiRequest({
      profile: {
        USER_TYPE: userType,
        ANNUALINCOME: expressInput.incomeRange || 'Not specified',
        MONTHLY_INCOME_KEY: expressInput.incomeRange ? `express_${expressInput.incomeRange}` : '',
        AGE_GROUP: 'Not specified',
        OCCUPATION: userType === 'BUSINESS' ? 'Business Owner' : 'Employee',
        PRODUCTGOAL: ['Financial Automation'],
      },
      prompt,
    });

    track(EVENTS.AUTH_MODAL_SHOWN, { flow: 'express' });
    setShowAuthModal(true);
  }

  const charCount = expressInput.text.length;
  const showPlaceholder = !expressInput.text;

  return (
    <>
      <div className="express-page">
        <div className="express-page__logo">
          <img src="/sequence-logo.png" alt="Sequence" className="express-page__logo-img" />
        </div>
        <div className="express-page__container">
          <div className="express-page__header">
            <h1 className="express-page__title">Tell AI How Your Money Should Work</h1>
            <p className="express-page__subtitle">
              Get a free financial setup tailored to your business needs and goals
            </p>
          </div>

          <div className="express-page__form">
            <div className="express-page__textarea-container">
              <textarea
                className="express-page__textarea"
                value={expressInput.text}
                onChange={handleTextChange}
                placeholder=" "
              />
              {showPlaceholder && (
                <div className="express-page__animated-placeholder">
                  <AnimatedPlaceholder text={displayText} isTyping={isTyping} />
                </div>
              )}
            </div>

            <div className="express-page__char-count">
              <span style={{ color: charCount < 20 || charCount > 1000 ? 'var(--color-error)' : undefined }}>
                {charCount} / 1000
              </span>
            </div>

            {error && (
              <p style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)' }}>
                {error}
              </p>
            )}

            <p className="express-page__disclaimer">
              Default numbers are based on Sequence's data from similar profiles.
              The more detail you share, the more tailored your setup will be.
            </p>

            <ExampleChips examples={EXAMPLE_CHIP_LABELS} onSelect={handleExampleClick} />

            <div className="express-page__actions">
              <Button
                variant="primary"
                size="lg"
                onClick={handleSubmit}
                disabled={charCount < 20 || submitting}
                className="express-page__cta"
                icon={
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                  </svg>
                }
              >
                Create My Financial Setup
              </Button>
            </div>
          </div>

          <div style={{ marginTop: 'var(--space-8)' }}>
            <TrustBadges />
          </div>
        </div>
      </div>

      {/* Section 1: How It Works */}
      <section className="express-landing express-landing--purple">
        <div className="express-landing__container express-landing__container--wide">
          <p className="express-landing__eyebrow">HOW IT WORKS</p>
          <h2 className="express-landing__headline">From your words to a working setup</h2>
          <div className="express-hiw">
            <div className="express-steps">
              <div className="express-step">
                <div className="express-step__number">1</div>
                <div>
                  <h3 className="express-step__title">Describe your business</h3>
                  <p className="express-step__desc">
                    Tell us how money flows through your business - revenue, expenses, taxes, owner's pay. Plain English, no forms.
                  </p>
                </div>
              </div>
              <div className="express-step">
                <div className="express-step__number">2</div>
                <div>
                  <h3 className="express-step__title">AI builds your setup</h3>
                  <p className="express-step__desc">
                    Our AI maps your finances into accounts, rules, and automations tailored to your business.
                  </p>
                </div>
              </div>
              <div className="express-step">
                <div className="express-step__number">3</div>
                <div>
                  <h3 className="express-step__title">Start automating</h3>
                  <p className="express-step__desc">
                    Connect your accounts on Sequence and let your money move where it should - taxes, expenses, your pay - on autopilot.
                  </p>
                </div>
              </div>
            </div>
            <div className="express-hiw__visual">
              <div className="express-hiw__frame">
                <svg className="express-hiw__map" viewBox="0 0 440 340" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Money In node - left side */}
                  <rect className="hiw-fade hiw-d1" x="20" y="120" width="150" height="80" rx="10" fill="white" stroke="#d1e3f8" strokeWidth="1.5" />
                  <rect className="hiw-fade hiw-d1" x="20" y="120" width="150" height="30" rx="10" fill="#dbeafe" />
                  <rect className="hiw-fade hiw-d1" x="20" y="140" width="150" height="10" fill="#dbeafe" />
                  <text className="hiw-fade hiw-d1" x="58" y="140" fill="#2563eb" fontSize="12" fontWeight="600" fontFamily="system-ui, sans-serif">Money In</text>
                  <text className="hiw-fade hiw-d1" x="95" y="180" textAnchor="middle" fill="#374151" fontSize="16" fontWeight="700" fontFamily="system-ui, sans-serif">$10,696</text>
                  <text className="hiw-fade hiw-d1" x="34" y="140" fill="#374151" fontSize="13">🏦</text>

                  {/* Curved lines from Money In to destinations */}
                  <path className="hiw-line hiw-d2" d="M170 140 C 240 140, 240 60, 280 60" stroke="#374151" strokeWidth="1.5" fill="none" />
                  <path className="hiw-line hiw-d3" d="M170 160 C 230 160, 240 160, 280 160" stroke="#374151" strokeWidth="1.5" fill="none" />
                  <path className="hiw-line hiw-d4" d="M170 180 C 240 180, 240 260, 280 260" stroke="#374151" strokeWidth="1.5" fill="none" />

                  {/* Arrow tips */}
                  <polygon className="hiw-fade hiw-d3" points="278,55 288,60 278,65" fill="#374151" />
                  <polygon className="hiw-fade hiw-d4" points="278,155 288,160 278,165" fill="#374151" />
                  <polygon className="hiw-fade hiw-d5" points="278,255 288,260 278,265" fill="#374151" />

                  {/* Bill Pay node */}
                  <rect className="hiw-fade hiw-d3" x="290" y="30" width="130" height="65" rx="10" fill="white" stroke="#e9d5ff" strokeWidth="1.5" />
                  <rect className="hiw-fade hiw-d3" x="290" y="30" width="130" height="25" rx="10" fill="#f3e8ff" />
                  <rect className="hiw-fade hiw-d3" x="290" y="47" width="130" height="8" fill="#f3e8ff" />
                  <text className="hiw-fade hiw-d3" x="330" y="48" fill="#7c3aed" fontSize="11" fontWeight="600" fontFamily="system-ui, sans-serif">Bill Pay</text>
                  <text className="hiw-fade hiw-d3" x="355" y="78" textAnchor="middle" fill="#374151" fontSize="14" fontWeight="700" fontFamily="system-ui, sans-serif">$3,209</text>
                  <text className="hiw-fade hiw-d3" x="303" y="48" fill="#374151" fontSize="12">💰</text>

                  {/* Payroll node */}
                  <rect className="hiw-fade hiw-d4" x="290" y="130" width="130" height="65" rx="10" fill="white" stroke="#e9d5ff" strokeWidth="1.5" />
                  <rect className="hiw-fade hiw-d4" x="290" y="130" width="130" height="25" rx="10" fill="#f3e8ff" />
                  <rect className="hiw-fade hiw-d4" x="290" y="147" width="130" height="8" fill="#f3e8ff" />
                  <text className="hiw-fade hiw-d4" x="330" y="148" fill="#7c3aed" fontSize="11" fontWeight="600" fontFamily="system-ui, sans-serif">Payroll</text>
                  <text className="hiw-fade hiw-d4" x="355" y="178" textAnchor="middle" fill="#374151" fontSize="14" fontWeight="700" fontFamily="system-ui, sans-serif">$4,278</text>
                  <text className="hiw-fade hiw-d4" x="303" y="148" fill="#374151" fontSize="12">💰</text>

                  {/* Opex node */}
                  <rect className="hiw-fade hiw-d5" x="290" y="230" width="130" height="65" rx="10" fill="white" stroke="#e9d5ff" strokeWidth="1.5" />
                  <rect className="hiw-fade hiw-d5" x="290" y="230" width="130" height="25" rx="10" fill="#f3e8ff" />
                  <rect className="hiw-fade hiw-d5" x="290" y="247" width="130" height="8" fill="#f3e8ff" />
                  <text className="hiw-fade hiw-d5" x="330" y="248" fill="#7c3aed" fontSize="11" fontWeight="600" fontFamily="system-ui, sans-serif">Opex</text>
                  <text className="hiw-fade hiw-d5" x="355" y="278" textAnchor="middle" fill="#374151" fontSize="14" fontWeight="700" fontFamily="system-ui, sans-serif">$3,209</text>
                  <text className="hiw-fade hiw-d5" x="303" y="248" fill="#374151" fontSize="12">💰</text>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Trust & Security */}
      <section className="express-landing">
        <div className="express-landing__container">
          <p className="express-landing__eyebrow">ZERO RISK</p>
          <h2 className="express-landing__headline">No login. No data shared. No catch.</h2>
          <div className="express-trust-cards">
            <div className="express-trust-card">
              <div className="express-trust-card__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <rect x="9" y="10" width="6" height="5" rx="1" />
                  <path d="M10 10V8a2 2 0 1 1 4 0v2" />
                </svg>
              </div>
              <h3 className="express-trust-card__title">No bank connection needed</h3>
              <p className="express-trust-card__desc">
                Your setup is built entirely from what you describe. We never ask for login credentials or access to your accounts.
              </p>
            </div>
            <div className="express-trust-card">
              <div className="express-trust-card__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                  <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                </svg>
              </div>
              <h3 className="express-trust-card__title">Your data stays private</h3>
              <p className="express-trust-card__desc">
                We don't store your financial details. Your input generates your setup and nothing else.
              </p>
            </div>
            <div className="express-trust-card">
              <div className="express-trust-card__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <h3 className="express-trust-card__title">100% free</h3>
              <p className="express-trust-card__desc">
                No hidden fees, no premium tier, no credit card required. Your financial setup is yours to keep.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Inspiration Gallery */}
      <section className="express-landing express-landing--tinted">
        <div className="express-landing__container express-landing__container--wide">
          <p className="express-landing__eyebrow">SEE WHAT'S POSSIBLE</p>
          <h2 className="express-landing__headline">Setups business owners are building</h2>
          <div className="express-gallery">
            {[
              { id: '466eea41-7a81-4b2d-acdf-307ca87881ff', title: 'Profits First Split', desc: 'Auto-split revenue into taxes, expenses, reserves, and owner\'s pay - then divide your pay into personal bills' },
              { id: '608db0ba-a76f-4a08-bbd4-7d1446536eb5', title: 'Creator Side Hustle', desc: 'Two income streams, one system - auto-split your 9-to-5 and side hustle into needs, savings, taxes, and fun money' },
              { id: '126a7bb8-b576-42d1-8ee2-f9a22d13d48c', title: 'Full Financial OS', desc: 'Business revenue flows to opex, taxes, profit, and your pay - then your pay auto-covers rent, groceries, debt, and savings' },
            ].map((card) => (
              <div key={card.id} className="express-gallery-card">
                <div className="express-gallery-card__preview">
                  <iframe
                    className="express-gallery-card__iframe"
                    src={`https://playground.getsequence.io/?id=${card.id}&embed=true`}
                    title={card.title}
                    loading="lazy"
                  />
                </div>
                <div className="express-gallery-card__body">
                  <h3 className="express-gallery-card__title">{card.title}</h3>
                  <p className="express-gallery-card__desc">{card.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 4: FAQ */}
      <section className="express-landing">
        <div className="express-landing__container">
          <p className="express-landing__eyebrow">QUESTIONS WE GET A LOT</p>
          <h2 className="express-landing__headline">Before you scroll back up...</h2>
          <div className="express-faq">
            {[
              {
                id: 'bank',
                q: 'Do I need to connect my bank account?',
                a: 'No. The AI builds your setup purely from what you describe. You only connect accounts later if you want Sequence to automate transfers for you. That\'s always your call.',
              },
              {
                id: 'numbers',
                q: 'What if my revenue changes month to month?',
                a: 'That\'s common for most businesses. Give us a rough range and the AI will build percentage-based rules that adapt to whatever comes in. You can always adjust later.',
              },
              {
                id: 'sell',
                q: 'Is this actually free?',
                a: 'Your financial setup is free - no catch. At the end, we\'ll show you how Sequence can run your setup automatically instead of you doing it manually. No pressure, and your setup is yours to keep either way.',
              },
              {
                id: 'business',
                q: 'Does this work for my type of business?',
                a: 'If money comes in and needs to go to different places - taxes, expenses, your pay - this works. We\'ve seen setups for consultants, agencies, e-commerce shops, contractors, freelancers, and everything in between.',
              },
            ].map((item) => (
              <div key={item.id} className="express-faq-item">
                <button
                  className="express-faq-item__trigger"
                  onClick={() => setOpenFaq(openFaq === item.id ? null : item.id)}
                >
                  <span>{item.q}</span>
                  <svg
                    className={`express-faq-item__chevron${openFaq === item.id ? ' express-faq-item__chevron--open' : ''}`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                <div className={`express-faq-item__content${openFaq === item.id ? ' express-faq-item__content--open' : ''}`}>
                  <p className="express-faq-item__answer">{item.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 5: Final CTA */}
      <section className="express-landing express-landing--dark">
        <div className="express-landing__container express-final-cta">
          <h2 className="express-landing__headline">Ready to put your finances on autopilot?</h2>
          <p className="express-final-cta__subtitle">
            Describe how your business works and get your free financial setup in seconds.
          </p>
          <Button
            variant="primary"
            size="lg"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            Create My Financial Setup
          </Button>
        </div>
      </section>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </>
  );
}
